/**
 * 品目の削除と復元（品目詳細。管理者のみ）。
 *
 * 削除は論理削除（functions/delete-dish.mjs）。品目一覧・評価の選択肢・熟達度から外れるだけで、
 * 素材・マニュアル・評価の記録は残り、誰がいつ消したかは実行履歴に残る。
 * 誤って消したときは、同じ画面の「元に戻す」で戻せる。
 *
 * 書き込みは Callable を通す（台帳への追記と同時に行うため。RenameDish と同じ理由）。
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useI18n } from "../lib/i18n";
import type { DishDoc } from "../lib/types";

const functions = getFunctions(app, "asia-northeast1");
const deleteDish = httpsCallable<
  { dishId: string; restore?: boolean; reason?: string },
  { ok: boolean; restored: boolean }
>(functions, "deleteDish");

/** 削除の確認欄。「この品目を削除する」を押すと開く */
export function DeleteDish({ dishId, dish, onClose }: { dishId: string; dish: DishDoc; onClose: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDelete() {
    setBusy(true);
    setError(null);
    try {
      await deleteDish({ dishId, reason: reason.trim() || undefined });
      navigate("/");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="card warn delete-dish">
      <p>
        <strong>{t("「{name}」を削除します。", { name: dish.name })}</strong>
      </p>
      <p className="small">
        {t("品目一覧・評価の選択肢・熟達度の表から外れます。素材・マニュアル・評価の記録は消えず、この画面の「元に戻す」で戻せます。誰が削除したかは実行履歴に残ります。")}
      </p>
      <label className="small">
        {t("削除の理由（任意）")}
        <input
          type="text"
          value={reason}
          placeholder={t("例: 別の品目として作り直したため")}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {error && <p className="error">{error}</p>}
      <div className="prep-import-row">
        <button className="danger" disabled={busy} onClick={() => void onDelete()}>
          {busy ? t("削除中…") : t("削除する")}
        </button>
        <button className="link" disabled={busy} onClick={onClose}>
          {t("やめる")}
        </button>
      </div>
    </div>
  );
}

/** 削除済みの品目を開いたときの表示。管理者には「元に戻す」を出す */
export function DeletedBanner({ dishId, dish, isAdmin }: { dishId: string; dish: DishDoc; isAdmin: boolean }) {
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onRestore() {
    setBusy(true);
    setError(null);
    try {
      await deleteDish({ dishId, restore: true });
      // 品目文書は onSnapshot で流れてくるので、表示はそのまま戻る
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const when = dish.deletedAt ? dish.deletedAt.toDate().toLocaleString(locale) : "";
  return (
    <div className="warn coverage-warn">
      <strong>
        {t("この品目は削除されています（{when}・{name}）。品目一覧には表示されません。", {
          when,
          name: dish.deletedByName ?? "-",
        })}
      </strong>
      {dish.deleteReason && (
        <p className="small">
          {t("削除の理由:")} {dish.deleteReason}
        </p>
      )}
      {isAdmin && (
        <div className="prep-import-row">
          <button disabled={busy} onClick={() => void onRestore()}>
            {busy ? t("戻しています…") : t("元に戻す")}
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
